/**
 * 최상단 요약 카드 섹션 (1. 전체 지표 + 상위 10 지표)
 * - 전체 그룹: 월간 검색량, 총 상품 수, 경쟁 강도 (3열 그리드)
 * - 상위 10 그룹: 평균가, 평균 리뷰 수, 평균 찜 수, 6개월 판매량 (4열 그리드)
 *
 * @param {object} report - 분석 리포트 데이터 (report.stats 배열 사용)
 */
import React from 'react';
import {
    BarChartOutlined,
    ShoppingOutlined,
    InfoCircleOutlined,
    WalletOutlined,
    MessageOutlined,
    HeartOutlined,
    AppstoreOutlined,
    TrophyOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { TrophyFilled } from '@ant-design/icons';
import { Tooltip } from 'antd';

const SummaryCards = ({ report }) => {
    const comp = report.competitors || [];
    const missingPrice = comp.some(c => c.price == null);
    const missingReview = comp.some(c => c.reviewCount == null);
    const missingWish = comp.some(c => c.wishCount == null);
    const missingPurchase = comp.some(c => c.purchaseCount == null);

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 4fr', gap: 24, marginBottom: 32 }}>
            {/* 전체 지표 그룹 */}
            <div className="summary-group-box">
                <div className="group-label">
                    <AppstoreOutlined /> 전체
                </div>
                <div className="top-summary-grid-3">
                    {[
                        {
                            icon: <BarChartOutlined />,
                            label: '월간 검색량',
                            value: report.stats?.[0]?.value || '0',
                            suffix: report.stats?.[0]?.suffix || '건',
                            color: 'c-blue',
                        },
                        {
                            icon: <ShoppingOutlined />,
                            label: '총 상품 수',
                            value: report.stats?.[1]?.value || '0',
                            suffix: report.stats?.[1]?.suffix || '개',
                            color: 'c-purple',
                        },
                        {
                            icon: <InfoCircleOutlined />,
                            label: '경쟁 강도',
                            value: report.stats?.[2]?.value || '0',
                            suffix: report.stats?.[2]?.suffix || '',
                            color: 'c-orange',
                        },
                    ].map((s, i) => (
                        <div key={i} className={`summary-card ${s.color}`}>
                            <div className="header">
                                <div className="icon-wrap">{s.icon}</div>
                                <span className="label">{s.label}</span>
                            </div>
                            <div className="value">
                                {s.value}
                                {s.suffix}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 상위 10 지표 그룹 */}
            <div className="summary-group-box">
                <div className="group-label">
                    <TrophyOutlined /> 상위 10
                </div>
                <div className="top-summary-grid-4">
                    {[
                        {
                            icon: <WalletOutlined />,
                            label: '평균가',
                            value: report.stats?.[3]?.value || '0',
                            suffix: report.stats?.[3]?.suffix || '원',
                            color: 'c-green',
                            missing: missingPrice,
                        },
                        {
                            icon: <MessageOutlined />,
                            label: '평균 리뷰 수',
                            value: report.stats?.[4]?.value || '0',
                            suffix: report.stats?.[4]?.suffix || '개',
                            color: 'c-indigo',
                            missing: missingReview,
                        },
                        {
                            icon: <HeartOutlined />,
                            label: '평균 찜 수',
                            value: report.stats?.[5]?.value || '0',
                            suffix: report.stats?.[5]?.suffix || '개',
                            color: 'c-pink',
                            missing: missingWish,
                        },
                        {
                            icon: <TrophyFilled />,
                            label: '6개월 판매량',
                            value: report.stats?.[8]?.value || '0',
                            suffix: report.stats?.[8]?.suffix || '건',
                            color: 'c-red',
                            missing: missingPurchase,
                        },
                    ].map((s, i) => (
                        <div key={i} className={`summary-card ${s.color}`}>
                            <div className="header">
                                <div className="icon-wrap">{s.icon}</div>
                                <span className="label">{s.label}</span>
                                {s.missing && (
                                    <Tooltip title="일부 상품 데이터 누락 — 수집된 항목 기준으로 계산됨">
                                        <WarningOutlined
                                            style={{
                                                color: '#f59e0b',
                                                fontSize: 12,
                                                marginLeft: 4,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            <div className="value">
                                {s.value}
                                {s.suffix}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SummaryCards;
